<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import { useDisplay, useRender } from '@components/composable';
import { hSlot, warn } from '@components/utils';

import { useInjectLayout } from '../CdsLayout/context';
import { useInjectMain } from '../CdsMain/context';

import type { PropType } from 'vue';

export const componentName = 'CdsPage';
export default defineComponent({
  name: componentName,
  props: {
    padding: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    styleFn: {
      type: Function as PropType<(offset: number, height: number) => string>,
      default: undefined
    }
  },
  emits: {},
  setup(props, { slots, expose }) {
    const layout = useInjectLayout();
    const main = useInjectMain();
    const display = useDisplay();

    if (!main) {
      warn('Page', 'CdsPage component must be used inside of a CdsMain component.');
      return () => {};
    }

    const style = computed(() => {
      const offset = (layout.header.space ? layout.header.size : 0) + (layout.footer.space ? layout.footer.size : 0);

      if (typeof props.styleFn === 'function') {
        const height = unref(layout.isContainer) ? unref(layout.containerHeight) : unref(display.screenHeight);

        return props.styleFn(offset, height);
      }

      return {
        minHeight: unref(layout.isContainer)
          ? unref(layout.containerHeight) - offset + 'px'
          : unref(display.screenHeight) === 0
          ? offset !== 0
            ? `calc(100vh - ${offset}px)`
            : '100vh'
          : unref(display.screenHeight) - offset + 'px'
      };
    });

    const classes = computed(() => ({
      'cds-page': true,
      'cds-page--padding': props.padding
    }));

    useRender(() =>
      h(
        'div',
        {
          class: unref(classes),
          style: unref(style)
        },
        hSlot(slots.default, [])
      )
    );

    expose({
      layout
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsPage';
</style>
